import {
  CAPABILITIES,
  CAPABILITY_CHANNEL_ROLE_KINDS,
  CAPABILITY_CHANNEL_ROLES,
  CAPABILITY_CHANNELS,
  CAPABILITY_IDS,
  CAPABILITY_SLOTS,
  POOLABLE_CONFIG_SHAPES,
  looksExternal,
  ROLES,
  ROLE_KINDS,
  type ConfigShape,
  type Role,
  type RoleKind,
} from './vocabulary.js';

export type ModuleFragmentation = 'fragmentable' | 'atomic';

export interface ModuleDescriptor {
  readonly id: string;
  readonly version: string;
  readonly fragmentation: ModuleFragmentation;
  readonly slots: readonly string[];
  readonly channels: readonly string[];
  readonly channelRoleKinds: Readonly<Record<string, readonly RoleKind[]>>;
  readonly channelRoles: Readonly<Record<string, readonly Role[]>>;
  /**
   * The complete config contract. Each key is accepted by validation and
   * rebuilt by the pool gate according to this closed shape; there is no
   * second capability-id table that can disagree with the registration.
   */
  readonly configShapes: Readonly<Record<string, ConfigShape>>;
}

const modules = new Map<string, ModuleDescriptor>();
const MODULE_ID = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
/** The same bound the schema applies. Two doors to one registry, and a name
 *  that is legal at one and not the other is a disagreement waiting to be
 *  found by whichever layer reads it second. */
const MODULE_ID_MAX = 64;
const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const SEMVER_MAX = 32;
/** A pooled integer production may carry at most eight bits. */
const INTEGER_CARDINALITY_MAX = 256;
const ENUM_CARDINALITY_MAX = 256;
const own = (value: object, key: PropertyKey): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

function fail(message: string): never {
  throw new Error(`Invalid module descriptor: ${message}`);
}

/**
 * The schema's own key syntax for a slot or channel name.
 *
 * Registration used to accept any non-empty string, while `schema.ts` refuses
 * anything outside this pattern — so a module could register successfully with
 * a contract **no authored spec could ever satisfy**, and the failure would
 * surface as an unexplained refusal at whatever spec tried to use it. Found by
 * @Codex on its own file.
 *
 * Two doors to one registry: a name legal at one and refused at the other is a
 * disagreement waiting to be discovered by whichever layer reads it second.
 */
const SLOT_OR_CHANNEL = /^[a-z][a-z0-9-]{0,23}$/;
const CONFIG_KEY = /^[a-zA-Z][a-zA-Z0-9]{0,31}$/;

function stringList(
  value: unknown, field: string, syntax?: RegExp,
): readonly string[] {
  if (!Array.isArray(value)) fail(`${field} must be an array of non-empty strings`);
  const entries = [...value];
  if (entries.some((item) => typeof item !== 'string' || item.length === 0)) {
    fail(`${field} must be an array of non-empty strings`);
  }
  if (new Set(entries).size !== entries.length) fail(`${field} must not contain duplicates`);
  if (syntax) {
    const bad = (entries as readonly string[]).filter((v) => !syntax.test(v));
    if (bad.length) {
      fail(`${field} names must match ${String(syntax)} — refused: ${bad.join(', ')}`);
    }
  }
  return entries as readonly string[];
}

function roleMap<K extends string>(
  value: unknown,
  field: string,
  channels: ReadonlySet<string>,
  allowed: ReadonlySet<string>,
): Readonly<Record<string, readonly K[]>> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    fail(`${field} must be an object`);
  }

  const canonical: Record<string, readonly K[]> = {};
  for (const [channel, entries] of Object.entries(value)) {
    if (!channels.has(channel)) fail(`${field}.${channel} names an undeclared channel`);
    const list = stringList(entries, `${field}.${channel}`);
    for (const entry of list) {
      if (!allowed.has(entry)) fail(`${field}.${channel} contains unknown value ${entry}`);
    }
    canonical[channel] = list as readonly K[];
  }
  return canonical;
}

function configShapeMap(value: unknown): Readonly<Record<string, ConfigShape>> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    fail('configShapes must be an object');
  }

  const canonical: Record<string, ConfigShape> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (!CONFIG_KEY.test(key)) {
      fail(`configShapes.${key} names a config key no authored spec can use`);
    }
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
      fail(`configShapes.${key} must be a config shape`);
    }
    const shape = raw as Record<string, unknown>;
    if (!own(shape, 'type')) {
      fail(`configShapes.${key} must declare its own shape type`);
    }
    if (shape['type'] === 'boolean') {
      if (Object.keys(shape).some((name) => name !== 'type')) {
        fail(`configShapes.${key} has fields a boolean shape does not declare`);
      }
      canonical[key] = { type: 'boolean' };
      continue;
    }
    if (shape['type'] === 'integer') {
      if (!own(shape, 'min') || !own(shape, 'max')
          || Object.keys(shape).some((name) => !['type', 'min', 'max'].includes(name))
          || !Number.isSafeInteger(shape['min'])
          || !Number.isSafeInteger(shape['max'])
          || (shape['min'] as number) > (shape['max'] as number)) {
        fail(`configShapes.${key} must declare its own integer min and max`);
      }
      const min = shape['min'] as number;
      const max = shape['max'] as number;
      if (max - min + 1 > INTEGER_CARDINALITY_MAX) {
        fail(`configShapes.${key} integer range is too wide to pool safely`);
      }
      canonical[key] = { type: 'integer', min, max };
      continue;
    }
    if (shape['type'] === 'enum') {
      if (!own(shape, 'values')
          || Object.keys(shape).some((name) => !['type', 'values'].includes(name))) {
        fail(`configShapes.${key} must declare its own enum values`);
      }
      const values = stringList(shape['values'], `configShapes.${key}.values`);
      if (values.length === 0 || values.length > ENUM_CARDINALITY_MAX
          || values.some((entry) => entry.length > 32)
          || values.some(looksExternal)) {
        fail(`configShapes.${key}.values must contain bounded, local enum options`);
      }
      canonical[key] = { type: 'enum', values: [...values] };
      continue;
    }
    fail(`configShapes.${key} has an unknown shape type`);
  }
  return canonical;
}

function validateDescriptor(value: ModuleDescriptor): ModuleDescriptor {
  if (value === null || typeof value !== 'object') fail('descriptor must be an object');

  // Read every top-level field once, then return a literal. Registration must
  // not validate one set of accessor/prototype values and snapshot another.
  const id = value.id;
  const version = value.version;
  const fragmentation = value.fragmentation;
  if (typeof id !== 'string' || !MODULE_ID.test(id) || id.length > MODULE_ID_MAX) {
    fail(`id must be a lowercase, hyphen-separated module name of at most `
      + `${MODULE_ID_MAX} characters`);
  }
  if (typeof version !== 'string' || version.length > SEMVER_MAX || !SEMVER.test(version)) {
    fail(`version must be a semantic version of at most ${SEMVER_MAX} characters`);
  }
  if (fragmentation !== 'fragmentable' && fragmentation !== 'atomic') {
    fail('fragmentation must be fragmentable or atomic');
  }

  const slots = stringList(value.slots, 'slots', SLOT_OR_CHANNEL);
  const channels = stringList(value.channels, 'channels', SLOT_OR_CHANNEL);
  const channelSet = new Set(channels);
  const channelRoleKinds = roleMap<RoleKind>(
    value.channelRoleKinds, 'channelRoleKinds', channelSet, new Set(ROLE_KINDS),
  );
  const channelRoles = roleMap<Role>(
    value.channelRoles, 'channelRoles', channelSet, new Set(ROLES),
  );
  const configShapes = configShapeMap(value.configShapes);

  return {
    id, version, fragmentation, slots, channels,
    channelRoleKinds, channelRoles, configShapes,
  };
}

function snapshot(descriptor: ModuleDescriptor): ModuleDescriptor {
  const copyMap = <K extends string>(map: Readonly<Record<string, readonly K[]>>) =>
    Object.freeze(Object.fromEntries(
      Object.entries(map).map(([channel, values]) => [channel, Object.freeze([...values])]),
    )) as Readonly<Record<string, readonly K[]>>;
  const shapes = configShapeMap(descriptor.configShapes);

  return Object.freeze({
    id: descriptor.id,
    version: descriptor.version,
    fragmentation: descriptor.fragmentation,
    slots: Object.freeze([...descriptor.slots]),
    channels: Object.freeze([...descriptor.channels]),
    channelRoleKinds: copyMap(descriptor.channelRoleKinds),
    channelRoles: copyMap(descriptor.channelRoles),
    configShapes: Object.freeze(Object.fromEntries(
      Object.entries(shapes).map(([key, shape]) => [
        key,
        Object.freeze(shape.type === 'enum'
          ? { type: 'enum', values: Object.freeze([...shape.values]) }
          : shape.type === 'integer'
            ? { type: 'integer', min: shape.min, max: shape.max }
            : { type: 'boolean' }),
      ]),
    )),
  });
}

/** Register a complete module manifest. Validation precedes the single write. */
export function registerModule(input: ModuleDescriptor): ModuleDescriptor {
  const descriptor = validateDescriptor(input);
  if (modules.has(descriptor.id)) throw new Error(`Module already registered: ${descriptor.id}`);
  // RETURNS the frozen snapshot, so a runtime registration keys its renderer
  // from exactly the descriptor that entered the spec registry rather than from
  // the caller's object. @Codex's, on task 0093: the two halves must agree on
  // `id@version` or the renderer is filed under a name nothing looks up.
  const registered = snapshot(descriptor);
  modules.set(descriptor.id, registered);
  return registered;
}

export function lookupModule(id: string): ModuleDescriptor | undefined {
  return modules.get(id);
}

export function registeredModuleIds(): readonly string[] {
  return [...modules.keys()];
}

for (const id of CAPABILITY_IDS) {
  registerModule({
    id,
    ...CAPABILITIES[id],
    slots: CAPABILITY_SLOTS[id],
    channels: CAPABILITY_CHANNELS[id],
    channelRoleKinds: CAPABILITY_CHANNEL_ROLE_KINDS[id],
    channelRoles: CAPABILITY_CHANNEL_ROLES[id],
    configShapes: POOLABLE_CONFIG_SHAPES[id],
  });
}
